import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Home, Clock3, Shield, Sparkles } from "lucide-react";
import Sidebar from "../components/Sidebar";
import MobileTopBar from "../components/MobileTopBar";
import StudioFooter from "../components/StudioFooter";
import { findService } from "../lib/services";
import { useOverrides, applySubOverride } from "../context/OverridesContext";

export default function ServicePage() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const service = findService(serviceSlug);
  const { overrides } = useOverrides();

  if (!service) {
    return (
      <div className="flex min-h-screen bg-brand-ink">
        <Sidebar />
        <main className="flex-1 p-10">
          <div className="font-serif-display text-3xl">Service not found</div>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-brand">
            Back to Dashboard
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-brand-ink">
      <Sidebar />
      <main className="flex-1 overflow-x-hidden">
        <MobileTopBar title="Studio" />
        <div className="mx-auto max-w-[1240px] px-5 py-8 sm:px-8 sm:py-10">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <Link to="/dashboard" className="inline-flex items-center gap-2 hover:text-brand">
              <Home className="h-3 w-3" />
              Dashboard
            </Link>
            <span className="text-brand-mute/40">/</span>
            <span className="text-brand">{service.label}</span>
          </div>

          {/* Header */}
          <div className="mt-6 flex flex-wrap items-start justify-between gap-6 rounded-md border border-brand-hair bg-brand-panel p-5 sm:gap-8 sm:p-8">
            <div className="max-w-[720px]">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                <span>Service {service.id}</span>
                <span className="text-brand-mute/40">—</span>
                <span>{service.subs.length} options</span>
              </div>
              <h1 className="mt-3 font-serif-display text-[52px] font-medium leading-[1] tracking-tight">
                {service.label}
              </h1>
              <p className="mt-4 max-w-[620px] text-[15px] leading-relaxed text-brand-ivory/70">
                {service.description}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.28em]">
                <span className="rounded-md bg-brand/15 px-3 py-1.5 text-brand">From {service.priceFrom}</span>
                <span className="rounded-md border border-brand-hair px-3 py-1.5 text-brand-ivory/70">Zero meetings required</span>
                <span className="rounded-md border border-brand-hair px-3 py-1.5 text-brand-ivory/70">{service.responseTime}</span>
              </div>
            </div>
            <div className="h-28 w-28 overflow-hidden rounded-md border border-brand-hair bg-brand-panel2">
              <img
                src={service.image}
                alt={service.label}
                className="h-full w-full object-cover opacity-90"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

          {/* Builder CTA */}
          {service.builderTitle && (
            <div className="relative mt-6 overflow-hidden rounded-md border border-brand-hair bg-brand-panel p-5 sm:p-8">
              <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand">
                  <Sparkles className="h-3.5 w-3.5" /> {service.builderKicker}
                </div>
                <h2 className="mt-3 font-serif-display text-[36px] font-medium leading-[1.05] tracking-tight text-brand-ivory">
                  {service.builderTitle}
                </h2>
                <p className="mt-3 max-w-[640px] text-[14px] leading-relaxed text-brand-ivory/65">
                  {service.builderCopy}
                </p>
              </div>
            </div>
          )}

          {/* Sub-service cards */}
          <div className="mt-10 flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Pick a sub-service
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Each has its own workspace
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {service.subs.map((rawSub) => {
              const sub = applySubOverride(rawSub, overrides);
              return (
              <button
                key={sub.code}
                onClick={() => navigate(`/service/${service.slug}/${sub.slug}`)}
                className="group flex flex-col overflow-hidden rounded-md border border-brand-hair bg-brand-panel text-left transition-colors hover:border-brand/60"
              >
                <div className="relative h-44 overflow-hidden bg-brand-panel2">
                  {sub.cardType === "logos" && sub.logos && sub.logos.length ? (
                    <div className="flex h-full w-full items-center justify-center gap-4 bg-white px-6 py-4">
                      {sub.logos.map((l, i) => (
                        <img
                          key={i}
                          src={l.src}
                          alt={l.alt || ""}
                          className="max-h-14 max-w-[45%] object-contain"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ))}
                    </div>
                  ) : sub.image ? (
                    <img
                      src={sub.image}
                      alt={sub.title}
                      className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        if (e.currentTarget.parentElement) {
                          e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-[#1a1815]", "to-[#0f0f0f]");
                        }
                      }}
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-[#1a1815] to-[#0f0f0f]" />
                  )}
                  <div className={`absolute inset-0 ${sub.cardType === "logos" ? "" : "bg-gradient-to-t from-brand-panel via-transparent to-transparent"}`} />
                  <div className={`absolute left-4 top-4 rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-[0.22em] backdrop-blur-sm ${sub.cardType === "logos" ? "bg-black/70 text-white" : "bg-black/50 text-brand-ivory"}`}>
                    {sub.code}
                  </div>
                  <div className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md bg-black/50 text-white backdrop-blur-sm transition-colors group-hover:bg-brand">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="font-serif-display text-[20px] leading-tight tracking-tight text-brand-ivory">
                    {sub.title}
                  </div>
                  <div className="mt-1 text-[12.5px] text-brand-ivory/60">{sub.subtitle}</div>
                  <div className="mt-5 flex items-center justify-between border-t border-brand-hair/60 pt-3">
                    <div className="font-mono text-[12px] text-brand-ivory">{sub.price}</div>
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-brand-mute">{sub.billing}</div>
                  </div>
                </div>
              </button>
              );
            })}
          </div>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Reassurance icon={<Clock3 className="h-4 w-4" />} title="Response within 24 hours" copy="Every brief is reviewed by a studio editor within a working day." />
            <Reassurance icon={<Shield className="h-4 w-4" />} title="Fixed scope. No surprises." copy="You approve scope before we touch a manuscript." />
            <Reassurance icon={<ArrowRight className="h-4 w-4" />} title="No calls. Just craft." copy="Async by default. Meetings only if you ask for one." />
          </div>

          <StudioFooter code={service.id} label={service.label} />
        </div>
      </main>
    </div>
  );
}

function Reassurance({ icon, title, copy }) {
  return (
    <div className="rounded-md border border-brand-hair bg-brand-panel p-5">
      <div className="flex items-center gap-2 text-brand">
        {icon}
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]">{title}</div>
      </div>
      <p className="mt-2 text-[12.5px] leading-relaxed text-brand-ivory/65">{copy}</p>
    </div>
  );
}
